import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { Container, Spinner, Alert, Button, Badge } from "react-bootstrap";
import logo from "../assets/logo.png";
import "./SupplierCertificate.css";

const SupplierCertificate = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchProfile = async () => {
    try {
      const res = await API.get("/supplier/profile");
      setProfile(res.data || null);
    } catch (err) {
      setError("Error loading certificate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const displayName = useMemo(
    () => profile?.companyName || profile?.supplierName || "Supplier",
    [profile]
  );

  const verificationCode = useMemo(() => {
    const baseName = (profile?.supplierName || profile?.companyName || "SUPPLIER")
      .trim()
      .split(/\s+/)[0]
      .toUpperCase();
    const mobile = String(profile?.contactMobile || "").replace(/\D/g, "").slice(0, 5) || "00000";
    return `${baseName}-${mobile}-AUTH`;
  }, [profile]);

  const issuedAt = useMemo(() => {
    if (profile?.certificate?.issuedAt) return new Date(profile.certificate.issuedAt);
    return new Date();
  }, [profile]);

  const buildCertificateHtml = () => `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Supplier Authorization Certificate</title>
        <link href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Noto+Sans:wght@400;500;700&display=swap" rel="stylesheet" />
        <style>
          :root {
            --primary: #ef4444;
            --bg: #f9f2f2;
            --parchment: #fff8f8;
            --border-red: #991b1b;
            --text: #111827;
            --muted: #6b7280;
          }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            background: var(--bg);
            font-family: "Newsreader", serif;
            color: var(--text);
          }
          .page { min-height: 100vh; display: flex; flex-direction: column; }
          header {
            padding: 16px 32px;
            background: #fff;
            border-bottom: 1px solid #f0f4f1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: sticky;
            top: 0;
          }
          header h2 { margin: 0; font-family: "Noto Sans", sans-serif; font-size: 16px; }
          main {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 40px 16px 64px;
          }
          .print-area {
            width: 100%;
            max-width: 900px;
            background: var(--parchment);
            position: relative;
            padding: 48px;
            border: 12px solid var(--border-red);
            box-shadow: 0 30px 60px rgba(0,0,0,0.15);
          }
          .watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.03;
            pointer-events: none;
          }
          .content {
            position: relative;
            z-index: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 20px;
          }
          .brand-row {
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: "Noto Sans", sans-serif;
            font-weight: 700;
            letter-spacing: 0.2em;
            color: var(--border-red);
            text-transform: uppercase;
            font-size: 14px;
          }
          .brand-row img { height: 48px; }
          .subtitle { font-family: "Noto Sans", sans-serif; font-size: 10px; letter-spacing: 0.2em; color: #6b7280; text-transform: uppercase; }
          .title { font-size: 46px; color: var(--border-red); font-style: italic; margin: 10px 0 4px; }
          .divider { width: 120px; height: 4px; background: rgba(239, 68, 68, 0.2); margin: 6px auto 0; }
          .recipient p { font-family: "Noto Sans", sans-serif; color: #6b7280; margin: 0 0 10px; }
          .recipient h2 { margin: 0 0 8px; font-size: 32px; font-family: "Noto Sans", sans-serif; text-decoration: underline; text-decoration-color: rgba(239, 68, 68, 0.2); text-underline-offset: 8px; }
          .recipient .desc { max-width: 640px; font-style: italic; color: #374151; line-height: 1.6; }
          .details {
            width: 100%;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
            border-top: 1px solid rgba(239, 68, 68, 0.15);
            border-bottom: 1px solid rgba(239, 68, 68, 0.15);
            padding: 18px 0;
          }
          .detail-label {
            font-family: "Noto Sans", sans-serif;
            font-size: 10px;
            color: var(--primary);
            letter-spacing: 0.2em;
            text-transform: uppercase;
            font-weight: 700;
          }
          .detail-value { font-family: "Noto Sans", sans-serif; font-size: 13px; font-weight: 700; }
          .footer {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .signature { text-align: right; }
          .signature .name { font-family: "Noto Sans", sans-serif; font-weight: 700; }
          .signature .line { height: 1px; width: 160px; background: rgba(239, 68, 68, 0.35); margin: 8px 0 6px auto; }
          .corners span { position: absolute; width: 80px; height: 80px; border: 4px solid rgba(239, 68, 68, 0.2); }
          .corners .tl { top: 8px; left: 8px; border-right: 0; border-bottom: 0; }
          .corners .tr { top: 8px; right: 8px; border-left: 0; border-bottom: 0; }
          .corners .bl { bottom: 8px; left: 8px; border-right: 0; border-top: 0; }
          .corners .br { bottom: 8px; right: 8px; border-left: 0; border-top: 0; }
          @media print {
            header { display: none !important; }
            body { background: #fff !important; }
            .print-area { box-shadow: none !important; border-width: 8px; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <header>
            <h2>Lotus Rice Mill Pvt. Ltd.</h2>
            <div>
              <button onclick="window.print()" style="margin-right:8px;padding:8px 12px;border:none;border-radius:8px;background:var(--primary);color:#fff;font-weight:700;cursor:pointer;">Print</button>
              <button onclick="window.print()" style="padding:8px 12px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;color:#111812;font-weight:700;cursor:pointer;">PDF</button>
            </div>
          </header>
          <main>
            <div class="print-area">
              <div class="watermark">
                <div style="font-size: 120px; font-weight: 800; color: #991b1b;">CERTIFIED</div>
              </div>
              <div class="content">
                <div class="brand-row">
                  <img src="${logo}" alt="Lotus Rice Mill Pvt. Ltd." />
                  Lotus Rice Mill Pvt. Ltd.
                </div>
                <div class="subtitle">Supplier Authorization</div>
                <div>
                  <div class="title">Certificate</div>
                  <div class="divider"></div>
                </div>
                <div class="recipient">
                  <p>This is to officially certify that</p>
                  <h2>${displayName}</h2>
                  <p class="desc">has been duly vetted and is hereby recognized as an authorized supplier partner for Lotus Rice Mill Pvt. Ltd.</p>
                </div>
                <div class="details">
                  <div>
                    <div class="detail-label">Supplier Code</div>
                    <div class="detail-value">${profile?.supplierCode || "N/A"}</div>
                  </div>
                  <div>
                    <div class="detail-label">GSTIN</div>
                    <div class="detail-value">${profile?.gstNumber || "N/A"}</div>
                  </div>
                  <div>
                    <div class="detail-label">Verification</div>
                    <div class="detail-value">${verificationCode}</div>
                  </div>
                  <div>
                    <div class="detail-label">Contact</div>
                    <div class="detail-value">${profile?.contactMobile || "N/A"}</div>
                  </div>
                </div>
                <div class="footer">
                  <div>
                    <div class="detail-label">Date of Issue</div>
                    <div class="detail-value">${issuedAt.toLocaleDateString()}</div>
                  </div>
                  <div class="signature">
                    <div class="name">Anil Saxena</div>
                    <div class="detail-label">Authorized Signatory</div>
                    <div class="line"></div>
                    <div class="detail-label">Lotus Rice Mill Pvt. Ltd.</div>
                  </div>
                </div>
                <div class="corners">
                  <span class="tl"></span>
                  <span class="tr"></span>
                  <span class="bl"></span>
                  <span class="br"></span>
                </div>
              </div>
            </div>
          </main>
        </div>
      </body>
    </html>
  `;

  const handleViewCertificate = () => {
    const w = window.open("", "_blank");
    w.document.write(buildCertificateHtml());
    w.document.close();
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="danger" />
        <p>Loading certificate…</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  const status = profile?.certificate?.issuedAt ? "generated" : "pending";

  return (
    <Container className="mt-4 supplier-cert-page">
      <div className="supplier-cert-header">
        <div>
          <h4 className="mb-1">Supplier Authorization Certificate</h4>
          <p className="supplier-cert-subtitle">Your approved supplier certificate with Lotus Rice Mill Pvt. Ltd.</p>
        </div>
        <div className="supplier-cert-actions">
          <Badge bg={status === "generated" ? "success" : "warning"}>
            {status === "generated" ? "Verified" : "Pending"}
          </Badge>
          {status === "generated" && (
            <Button variant="danger" onClick={handleViewCertificate}>View / Print</Button>
          )}
        </div>
      </div>

      <div className="supplier-cert-preview">
        <div className="supplier-cert-paper">
          <div className="supplier-cert-watermark">
            <div style={{ fontSize: 120, fontWeight: 800 }}>CERTIFIED</div>
          </div>
          <div className="supplier-cert-content">
            <div className="supplier-cert-brand">
              <div className="supplier-cert-brand-row">
                <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." />
                Lotus Rice Mill Pvt. Ltd.
              </div>
              <div className="supplier-cert-subline">Supplier Authorization</div>
            </div>
            <div>
              <div className="supplier-cert-title">Certificate</div>
              <div className="supplier-cert-divider"></div>
            </div>
            <div className="supplier-cert-recipient">
              <p>This is to officially certify that</p>
              <h2>{displayName}</h2>
              <p className="desc">has been duly vetted and is hereby recognized as an authorized supplier partner for Lotus Rice Mill Pvt. Ltd.</p>
            </div>
            <div className="supplier-cert-details">
              <div>
                <div className="supplier-detail-label">Supplier Code</div>
                <div className="supplier-detail-value">{profile?.supplierCode || "N/A"}</div>
              </div>
              <div>
                <div className="supplier-detail-label">GSTIN</div>
                <div className="supplier-detail-value">{profile?.gstNumber || "N/A"}</div>
              </div>
              <div>
                <div className="supplier-detail-label">Verification</div>
                <div className="supplier-detail-value">{verificationCode}</div>
              </div>
              <div>
                <div className="supplier-detail-label">Contact</div>
                <div className="supplier-detail-value">{profile?.contactMobile || "N/A"}</div>
              </div>
            </div>
            <div className="supplier-cert-footer">
              <div>
                <div className="supplier-detail-label">Date of Issue</div>
                <div className="supplier-detail-value">{issuedAt.toLocaleDateString()}</div>
              </div>
              <div className="supplier-cert-signature">
                <div className="name">Anil Saxena</div>
                <div className="supplier-detail-label">Authorized Signatory</div>
                <div className="line"></div>
                <div className="supplier-detail-label">Lotus Rice Mill Pvt. Ltd.</div>
              </div>
            </div>
            <div className="supplier-cert-corners">
              <span className="tl"></span>
              <span className="tr"></span>
              <span className="bl"></span>
              <span className="br"></span>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SupplierCertificate;
